import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

export default class extends Controller {
  static values = { 
    assetId: String,
    demoId: Number,
    resourceId: Number,
    referrer: { type: String, default: "player" },
    locale: { type: String, default: "en" }
  };
  static targets = ['mainControl','progressSlider', 'currentTime','duration', 'asset'];

  // Audio engagement tracking properties - using time ranges for accurate tracking
  listenedRanges = [] // Array of [start, end] time ranges
  currentRangeStart = null // Start time of current listening session
  lastRecordedTime = null // Last time we recorded

  assetTargetConnected() {
    this.assetTarget.ontimeupdate = this.updateProgressSlider.bind(this);
    this.assetTarget.onloadedmetadata = this.displayAssetTimes.bind(this);
    this.assetTarget.onended = this.handleEnded.bind(this);
  }

  connect() {
    // Track listen time before page unload (external navigation, tab close)
    if (this.demoIdValue === 0) {
      this.beforeUnloadHandler = () => {
        this.trackListenTimeBeforeUnload()
      }
      window.addEventListener('beforeunload', this.beforeUnloadHandler)
      window.addEventListener('pagehide', this.beforeUnloadHandler)

      // Track listen time before Turbo navigation (back button, internal links)
      this.turboBeforeVisitHandler = () => {
        this.trackListenTimeBeforeUnload()
      }
      document.addEventListener('turbo:before-visit', this.turboBeforeVisitHandler)
      document.addEventListener('turbo:before-render', this.turboBeforeVisitHandler)
    }
  }

  play(_event) {
    this.#toggleMainControl();
    this.assetTarget.play();
    this.logEvent('play')
    this.startTracking()
  }
  
  pause(_event) {
    this.#toggleMainControl();
    this.assetTarget.pause();
    this.logEvent('pause')
    this.stopTracking()
    this.trackListenTime()
  }

  handleEnded(_event) {
    this.#toggleMainControl();
    this.logEvent('ended')
    this.stopTracking()
    this.trackListenTime()
  }

  rewind(_event) {
    const currentTime = this.assetTarget.currentTime;
    this.assetTarget.currentTime = Math.max(0, currentTime - this.#skipTime);
    this.updateProgressSlider();
    this.logEvent('rewind');
  }

  fastForward(_event) {
    const currentTime = this.assetTarget.currentTime;
    const duration = this.assetTarget.duration;

    if (!Number.isFinite(duration)) {
      this.logEvent('fast_forward');
      return;
    }

    this.assetTarget.currentTime = Math.min(duration, currentTime + this.#skipTime);
    this.updateProgressSlider();
    this.logEvent('fast_forward');
  }

  updateProgressSlider(_event) {
    let currentTime = this.assetTarget.currentTime;
    let duration = this.assetTarget.duration;
    let newValue = (currentTime / duration) * 100 + "%";

    this.progressSliderTarget.style.width = newValue;
    this.progressSliderTarget.style.transitionDuration = "250ms";
    this.currentTimeTarget.innerHTML = this.#formatTime(currentTime);
    
    // Update listened time tracking (only for non-demo audio)
    if (this.demoIdValue === 0) {
      this.updateListenedTime()
    }
  }

  displayAssetTimes(_event) {
    this.durationTarget.innerHTML = this.#formatTime(this.assetTarget.duration);
  }

  seek(event) {
    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * this.assetTarget.duration;
    
    this.assetTarget.currentTime = newTime;
    this.updateProgressSlider();
    this.logEvent('seek');
    this.handleSeek();
  }

  //private
  #skipTime = 10;

  #toggleMainControl()  {
    this.mainControlTargets.forEach((element, _index) => {
      element.classList.toggle('hidden')
    });
  }

  #formatTime(currentTime) {
    const minutes = Math.floor(currentTime / 60);
    const seconds = Math.floor(currentTime % 60);
  
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
    const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds;
  
    return `${formattedMinutes}:${formattedSeconds}`;
  }

  async logEvent(event_type) {
    const payload = {
      event: `audio.${event_type}`,
      time: this.assetTarget.currentTime,
      resource_id: this.resourceIdValue,
      referrer: this.referrerValue,
      locale: this.localeValue
    };

    // If no demoId, log to user_activities
    if (this.demoIdValue !== 0) {
      await post(`/demos/${this.demoIdValue}/activity`, {
        body: JSON.stringify(payload)
      });
    } else {
      await post('/user_activities/activity', {
        body: JSON.stringify(payload)
      });
    }
  }

  startTracking() {
    if (this.demoIdValue !== 0 || !this.assetTarget) return
    const currentTime = this.assetTarget.currentTime
    this.currentRangeStart = currentTime
    this.lastRecordedTime = currentTime
  }

  stopTracking() {
    if (this.demoIdValue !== 0 || this.currentRangeStart === null || !this.assetTarget) return
    const currentTime = this.assetTarget.currentTime
    
    // Only add range if there was actual playback
    if (currentTime > this.currentRangeStart) {
      this.listenedRanges.push([this.currentRangeStart, currentTime])
    }
    
    this.currentRangeStart = null
    this.lastRecordedTime = null
  }

  handleSeek() {
    if (this.demoIdValue !== 0 || !this.assetTarget) return
    
    const currentTime = this.assetTarget.currentTime
    
    // If we were tracking, close the current range
    if (this.currentRangeStart !== null && this.lastRecordedTime !== null) {
      // Only add if we listened to something before seeking
      if (this.lastRecordedTime > this.currentRangeStart) {
        this.listenedRanges.push([this.currentRangeStart, this.lastRecordedTime])
      }
    }
    
    // Start new range at the seeked position if playing
    if (!this.assetTarget.paused) {
      this.currentRangeStart = currentTime
      this.lastRecordedTime = currentTime
    } else {
      this.currentRangeStart = null
      this.lastRecordedTime = null
    }
  }

  updateListenedTime() {
    if (this.demoIdValue !== 0 || this.currentRangeStart === null || !this.assetTarget) return
    
    const currentTime = this.assetTarget.currentTime
    
    // Detect discontinuities (like seeking during playback without triggering seeked event)
    if (this.lastRecordedTime !== null && Math.abs(currentTime - this.lastRecordedTime) > 1.5) {
      // Close current range and start new one
      if (this.lastRecordedTime > this.currentRangeStart) {
        this.listenedRanges.push([this.currentRangeStart, this.lastRecordedTime])
      }
      this.currentRangeStart = currentTime
    }
    
    this.lastRecordedTime = currentTime
  }

  async trackListenTime() {
    // Only track listen time for non-demo audio (UserActivity, not DemoActivity)
    if (this.demoIdValue === 0 && this.listenedRanges.length > 0 && this.assetTarget) {
      const audioDuration = this.assetTarget.duration
      
      const payload = {
        event: 'audio.listen_time',
        resource_id: this.resourceIdValue,
        referrer: this.referrerValue,
        locale: this.localeValue,
        listened_ranges: this.listenedRanges,
        audio_duration_seconds: Math.round(audioDuration * 100) / 100,
        current_time: Math.round(this.assetTarget.currentTime * 100) / 100
      };

      await post('/user_activities/activity', {
        body: JSON.stringify(payload)
      });
      
      // Clear ranges after successful send to avoid duplicate tracking
      this.listenedRanges = [];
      this.currentRangeStart = null;
      this.lastRecordedTime = null;
    }
  }

  trackListenTimeBeforeUnload() {
    // Use sendBeacon for reliable delivery during page unload
    if (this.demoIdValue !== 0 || !this.assetTarget) return;

    // Important: Stop tracking to capture current listening session
    // This adds the current range (if playing) to listenedRanges
    this.stopTracking();

    // Only send if there are ranges to track
    if (this.listenedRanges.length > 0) {
      const audioDuration = this.assetTarget.duration
      
      const payload = {
        event: 'audio.listen_time',
        resource_id: this.resourceIdValue,
        referrer: this.referrerValue,
        locale: this.localeValue,
        listened_ranges: this.listenedRanges,
        audio_duration_seconds: Math.round(audioDuration * 100) / 100,
        current_time: Math.round(this.assetTarget.currentTime * 100) / 100
      };

      // sendBeacon requires a Blob with proper content type
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon('/user_activities/activity', blob);
      
      // Clear ranges after sending
      this.listenedRanges = [];
      this.currentRangeStart = null;
      this.lastRecordedTime = null;
    }
  }

  disconnect() {
    // Clean up event listeners when controller is disconnected
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler)
      window.removeEventListener('pagehide', this.beforeUnloadHandler)
    }

    if (this.turboBeforeVisitHandler) {
      document.removeEventListener('turbo:before-visit', this.turboBeforeVisitHandler)
      document.removeEventListener('turbo:before-render', this.turboBeforeVisitHandler)
    }
  }
}
